import {
  CloudDownload,
  Edit,
  FavoriteBorder,
  Favorite,
  AssignmentLate,
  Construction
} from '@mui/icons-material';
import {
  Tooltip,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  useMediaQuery,
  lighten
} from '@mui/material';
import orange from '@mui/material/colors/orange';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { format, parseISO } from 'date-fns';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Loader,
  OrderManagementPage,
  PageTitle,
  Pagination,
  Table,
  SearchSection
} from '../../../components';
import { UserContext } from '../../../context';
import { GlobalContext } from '../../../context/global';
import {
  filtersPayload,
  sortingPayload,
  updateFilters,
  updateSort
} from '../../../context/actions';
import {
  enableAllowedUsers,
  getAllowedUsers,
  getOrders,
  removeOrderFavorite,
  updateOrderStatus
} from '../../../fetch';
import {
  formatDate,
  formatMoney,
  getEnvironment,
  handleSortDirection,
  UNDEFINED_PART
} from '../../../helpers';
import {
  IAllowedUsers,
  IOrderDetail,
  IOrderType,
  IDateRange,
  ISortDirection
} from '../../../models';
import { MobileOrderManagement } from './MobileOrderManagement';
import { OrderManagementFilters } from './OrderManagementFilters';
import { FavoriteModal } from './FavoriteModal';
import { formatCSVColumns } from './order-management-util';

const PAGE_NAME = 'order-management';
interface IOrderManagementFilters {
  filters?: {
    orderSearch?: string;
    vendorId?: string;
    neighborhoodId?: string;
    lotNumber?: string;
    orderPlacer?: string;
    descriptionId?: string;
    orderType?: string;
    changeOrder?: boolean;
    active?: boolean;
    status?: 'New' | 'Approved' | 'Remediating' | 'Placed' | 'Delivered' | 'Cancelled' | string;
    startDate?: Date;
    endDate?: Date;
  };
}

interface IOrderManagementSorting {
  sort?: string;
  sortDirection?: { [key: string]: ISortDirection };
}

export const OrderManagement = () => {
  const history = useHistory();
  const { isBillingAdmin, isFinancialAdmin } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [orderTypes, setParentOrderTypes] = useState<IOrderType[] | null>(null);
  const [isLoadingOrders, setLoadingOrders] = useState<boolean>(false);
  const [orders, setOrders] = useState<IOrderDetail[]>([]);
  const [isLoadingCSVData, setIsLoadingCSVData] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<IOrderDetail[]>([]);

  const { filterValues, dispatchFilters, dispatchSorting, sortingValues } =
    useContext(GlobalContext);

  const currentFilterState = filterValues.find(state => state?.pageName === PAGE_NAME);

  const persistentFilters = useMemo<IOrderManagementFilters>(
    () =>
      currentFilterState
        ? (currentFilterState as IOrderManagementFilters)
        : ({} as IOrderManagementFilters),
    [currentFilterState]
  );

  const currentSortingState = sortingValues.find(state => state?.pageName === PAGE_NAME);

  const persistentSorting = useMemo<IOrderManagementSorting>(
    () =>
      currentSortingState
        ? (currentSortingState as IOrderManagementSorting)
        : ({} as IOrderManagementSorting),
    [currentSortingState]
  );

  const handleUpdateFilters = (payload: filtersPayload) => {
    dispatchFilters(updateFilters(payload));
  };

  const handleUpdateSorting = (payload: sortingPayload) => {
    dispatchSorting(updateSort(payload));
  };

  const [selectedVendorId, setSelectedVendorId] = useState<string>(
    persistentFilters?.filters?.vendorId ?? ''
  );

  const [selectedOwner, setSelectedOwner] = useState<string>(
    persistentFilters?.filters?.orderPlacer ?? ''
  );

  const [selectedOrderType, setSelectedOrderType] = useState<string>(
    persistentFilters?.filters?.orderType ?? ''
  );

  const [selectedDescription, setSelectedDescription] = useState<string>(
    persistentFilters?.filters?.descriptionId ?? ''
  );

  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string>(
    persistentFilters?.filters?.neighborhoodId ?? ''
  );

  const [selectedLotId, setSelectedLotId] = useState<string>(
    persistentFilters?.filters?.lotNumber ?? ''
  );

  const [selectedStatus, setSelectedStatus] = useState<string>(
    persistentFilters?.filters?.status ?? ''
  );

  const [selectedDateRange, setSelectedDateRange] = useState<IDateRange>({
    startDate: persistentFilters?.filters?.startDate
      ? new Date(persistentFilters.filters.startDate)
      : null,
    endDate: persistentFilters?.filters?.endDate
      ? new Date(persistentFilters.filters.endDate)
      : null,
    key: 'selection',
    inputValue: `${
      persistentFilters?.filters?.startDate
        ? format(new Date(persistentFilters.filters.startDate), 'M/d/yyyy')
        : ''
    }${persistentFilters?.filters?.startDate && persistentFilters?.filters?.endDate ? ' - ' : ''}${
      persistentFilters?.filters?.endDate
        ? format(new Date(persistentFilters.filters.endDate), 'M/d/yyyy')
        : ''
    }`
  });

  const [searchValue, setSearch] = useState<string>(persistentFilters?.filters?.orderSearch ?? '');
  const [searchedValue, setSearchedValue] = useState<string>(
    persistentFilters?.filters?.orderSearch ?? ''
  );

  const [page, setPage] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [recordCount, setRecordCount] = useState<number>(0);
  const selectedSort = useMemo<string>(
    () => persistentSorting.sort ?? 'Date',
    [persistentSorting.sort]
  );
  const sortDirection = useMemo<{
    blinqId?: ISortDirection;
    Date?: ISortDirection;
    Description?: ISortDirection;
    Job?: ISortDirection;
    LotNumber?: ISortDirection;
    OrderNumbers?: ISortDirection;
    OrderPlacer?: ISortDirection;
    OrderType?: ISortDirection;
    Status?: ISortDirection;
    Total?: ISortDirection;
  }>(
    () =>
      persistentSorting?.sortDirection && Object.keys(persistentSorting?.sortDirection).length > 0
        ? persistentSorting?.sortDirection
        : // Value here just to make sure that the sorting is not empty
          { Date: 'Desc' },
    [persistentSorting.sortDirection]
  );
  const [isActive, setActive] = useState<boolean>(persistentFilters?.filters?.active ?? true);
  const [isAllSelected, setSelectedAll] = useState(false);
  const [isBulkUpdating, setBulkUpdating] = useState(false);
  const [currentFavoriteOrder, setCurrentFavoriteOrder] = useState<IOrderDetail | null>(null);
  const [isRemovingFavorite, setRemovingFavorite] = useState<boolean>(false);
  const [isLoadingAllowedUsers, setIsLoadingAllowedUsers] = useState(false);
  const [allowedUsers, setAllowedUsers] = useState<IAllowedUsers[]>(null);
  const [selectedAllowedUsers, setSelectedAllowedUsers] = useState<string[]>([]);
  const [initialSelectedAllowedUsers, setInitialSelectedAllowedUsers] = useState<string[]>([]);
  const fetchAllowedUsers = async () => {
    try {
      setIsLoadingAllowedUsers(true);
      const res = await getAllowedUsers();
      setAllowedUsers(res);
      const defaultAllowedUsers = res.filter(user => user.enabled).map(user => user.accountName);
      setSelectedAllowedUsers(defaultAllowedUsers);
      setInitialSelectedAllowedUsers(defaultAllowedUsers);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingAllowedUsers(false);
    }
  };

  const [isSharing, setIsSharing] = useState(false);
  const handleShare = async () => {
    if (!deepEqual(initialSelectedAllowedUsers, selectedAllowedUsers)) {
      setIsSharing(true);
      try {
        await enableAllowedUsers(selectedAllowedUsers);
        fetchOrders();
        fetchAllowedUsers();
      } catch (error) {
        console.log(error);
      } finally {
        setIsSharing(false);
      }
    }
  };

  const fetchOrders = async (clearSearch?: boolean) => {
    try {
      setLoadingOrders(true);
      const res = await getOrders({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        ...persistentFilters.filters,
        active: isActive
      });
      if (clearSearch || !searchValue) {
        setSearchedValue('');
        setSearch('');
      } else if (searchValue) {
        setSearchedValue(searchValue);
      }
      setOrders(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
      enqueueSnackbar('Error loading orders, please refresh and try again.', {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchAllOrders = async (clearSearch?: boolean) => {
    try {
      setIsLoadingCSVData(true);
      const res = await getOrders({
        page: 1,
        perPage: null,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        ...persistentFilters.filters
      });
      return res.records;
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingCSVData(false);
    }
  };

  useEffect(() => {
    fetchAllowedUsers();
  }, []);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistentFilters, page, perPage, sortDirection, selectedSort, isActive]);

  const isMobile = useMediaQuery('(max-width: 1400px)');
  const shouldShowSubSelectorDivider = useMediaQuery('(min-width: 500px)');

  const handleClickColumn = (column: string) => {
    const directionOfSort = handleSortDirection(sortDirection?.[column]);
    handleUpdateSorting({
      sort: directionOfSort ? column : '',
      sortDirection: {
        [column]: directionOfSort
      },
      pageName: PAGE_NAME,
      environment: getEnvironment()
    });
  };

  const handleBulkStatusChange = async status => {
    setBulkUpdating(true);
    try {
      // Select only the orders that can be updated
      // Loop through and make these calls consecutively, to avoid race conditions on the backend
      let i = 0;
      while (i <= selectedOrders.length - 1) {
        try {
          const res = await updateOrderStatus({
            fieldOrderId: selectedOrders[i].fieldOrderId,
            status,
            comment: selectedOrders[i].currentStatus.comment
          });
          if (res && res.Detail) {
            enqueueSnackbar(`Error, ${res.Detail}`, {
              variant: 'error'
            });
          } else if (
            res &&
            res.Errors &&
            Object.values(res.Errors)[0] &&
            Object.values(Object.values(res.Errors)[0])[0]
          ) {
            enqueueSnackbar(
              `Error, ${selectedOrders[i].fieldOrderId}-${
                Object.values(Object.values(res.Errors)[0])[0]
              }`,
              {
                variant: 'error'
              }
            );
          } else {
            enqueueSnackbar(`Order ${selectedOrders[i].fieldOrderId} Updated!`, {
              variant: 'success'
            });
          }
        } catch (e) {
          enqueueSnackbar(
            `Order ${selectedOrders[i].fieldOrderId} failed to update. Please try again.`,
            {
              variant: 'error'
            }
          );
        }
        // Update once
        i++;
      }
    } catch (error) {
      console.log(error);
    } finally {
      fetchOrders(true);
      setBulkUpdating(false);
      setSelectedOrders([]);
      setSelectedAll(false);
    }
  };
  const handleFavoriteClick = async (original: IOrderDetail) => {
    if (original.isFavorite) {
      const result = window.confirm('Are you sure you want to remove this favorite?');

      if (result) {
        setRemovingFavorite(true);
        try {
          await removeOrderFavorite(original.fieldOrderId);
          enqueueSnackbar('Favorite order removed!', {
            variant: 'success'
          });
          fetchOrders();
        } catch (error) {
          enqueueSnackbar('Error removing favorite order, please refresh and try again.', {
            variant: 'error'
          });
        } finally {
          setRemovingFavorite(false);
        }
      }
    } else {
      setCurrentFavoriteOrder(original);
    }
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActive(event.target.checked);
  };

  const columns = useMemo(() => {
    return [
      {
        id: 'checkbox',
        accessor: '',
        Header: '',
        overrideWidth: 50,
        sort: false,
        Cell: ({ cell }) => {
          const order: IOrderDetail = cell.row.original;
          const ids = selectedOrders.map(order => order.fieldOrderId);
          const isSelected = ids.includes(order.fieldOrderId);
          const showCheckbox = order.canApproveOrder && order.parts.length > 0;
          const showWarning = order.potentialDuplicateOrderIds.length > 0;

          if (showWarning) {
            return (
              <Tooltip title="This order could be a duplicate">
                <AssignmentLate />
              </Tooltip>
            );
          } else {
            return (
              showCheckbox && (
                <FormControlLabel
                  classes={{
                    label: classes.tableCheckbox
                  }}
                  color="primary"
                  control={
                    <Checkbox
                      checked={isSelected}
                      id="orders-checkbox"
                      onChange={() => {
                        isSelected
                          ? setSelectedOrders(
                              selectedOrders.filter(
                                selectedOrder => selectedOrder.fieldOrderId !== order.fieldOrderId
                              )
                            )
                          : setSelectedOrders([...selectedOrders, order]);
                      }}
                    />
                  }
                  label=""
                />
              )
            );
          }
        }
      },
      {
        Header: 'Date',
        accessor: 'dateOrdered',
        isServerSorted: selectedSort === 'Date',
        isServerSortedDesc: sortDirection.Date === 'Desc',
        handleClickColumn: () => handleClickColumn('Date'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IOrderDetail } };
        }) => {
          return <>{original.dateOrdered ? formatDate(original.dateOrdered) : null}</>;
        }
      },
      {
        Header: 'Order No.',
        accessor: 'sageOrderNumbers',
        isServerSorted: selectedSort === 'OrderNumbers',
        isServerSortedDesc: sortDirection.OrderNumbers === 'Desc',
        handleClickColumn: () => handleClickColumn('OrderNumbers'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IOrderDetail } };
        }) => {
          return (
            <>
              {original.sageOrderNumbers &&
                original.sageOrderNumbers.length > 0 &&
                original.sageOrderNumbers.map((sageOrderNumber, index) => {
                  return (
                    <span key={`${index}`}>
                      {sageOrderNumber}
                      {index !== original.sageOrderNumbers.length - 1 ? ', ' : ''}
                    </span>
                  );
                })}
            </>
          );
        }
      },
      {
        Header: 'BLinq No.',
        accessor: 'fieldOrderId',
        isServerSorted: selectedSort === 'BlinqId',
        isServerSortedDesc: sortDirection.blinqId === 'Desc',
        handleClickColumn: () => handleClickColumn('BlinqId')
      },
      {
        Header: 'Order Type',
        accessor: 'type',
        isServerSorted: selectedSort === 'OrderType',
        isServerSortedDesc: sortDirection.OrderType === 'Desc',
        handleClickColumn: () => handleClickColumn('OrderType'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: any } };
        }) => {
          const order: IOrderDetail = original;
          const typeName =
            orderTypes?.find(orderType => order.type === orderType.sageId)?.typeName ?? '';
          return <p className={clsx({ [classes.backcharge]: order.isBackcharge })}>{typeName}</p>;
        }
      },
      {
        Header: 'Order Desc.',
        accessor: 'description',
        isServerSorted: selectedSort === 'Description',
        isServerSortedDesc: sortDirection.Description === 'Desc',
        handleClickColumn: () => handleClickColumn('Description'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: any } };
        }) => {
          return <>{original.description ? original.description.name : ''}</>;
        }
      },
      {
        Header: 'Job',
        accessor: 'job',
        isServerSorted: selectedSort === 'Job',
        isServerSortedDesc: sortDirection.Job === 'Desc',
        handleClickColumn: () => handleClickColumn('Job')
      },
      {
        Header: 'Vendor',
        accessor: 'vendors',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IOrderDetail } };
        }) => {
          return (
            <>
              {original.vendors?.map((vendor, index) => {
                return (
                  <span key={`${index}`}>
                    {vendor.vendorName}
                    {index !== original.vendors.length - 1 ? ', ' : ''}
                  </span>
                );
              })}
            </>
          );
        }
      },
      {
        Header: 'Owner',
        accessor: 'orderPlacer',
        isServerSorted: selectedSort === 'OrderPlacer',
        isServerSortedDesc: sortDirection.OrderPlacer === 'Desc',
        handleClickColumn: () => handleClickColumn('OrderPlacer')
      },
      {
        Header: 'Parts',
        accessor: 'parts',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IOrderDetail } };
        }) => {
          return (
            <Tooltip
              title={
                <>
                  {original.parts &&
                    original.parts.length > 0 &&
                    original.parts.map((part, index) => {
                      return (
                        <span
                          key={`${index}`}
                          className={
                            part.partName === UNDEFINED_PART ? classes.undefinedPart : undefined
                          }
                        >
                          {part.partName}
                          {index !== original.parts.length - 1 ? ', ' : ''}
                        </span>
                      );
                    })}
                </>
              }
            >
              <Construction />
            </Tooltip>
          );
        }
      },
      {
        Header: 'Total',
        accessor: 'totalCost',
        isServerSorted: selectedSort === 'Total',
        isServerSortedDesc: sortDirection.Total === 'Desc',
        handleClickColumn: () => handleClickColumn('Total'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IOrderDetail } };
        }) => {
          return <>{formatMoney(original.totalCost)}</>;
        }
      },
      {
        Header: 'Status',
        accessor: 'status',
        overrideWidth: 100,
        isServerSorted: selectedSort === 'Status',
        isServerSortedDesc: sortDirection.Status === 'Desc',
        handleClickColumn: () => handleClickColumn('Status'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IOrderDetail } };
        }) => {
          return (
            <span
              className={
                original.currentStatus &&
                original.currentStatus.status &&
                original.currentStatus &&
                original.currentStatus.status === 'Remediating'
                  ? classes.statusRed
                  : null
              }
            >
              {original.currentStatus && original.currentStatus.status
                ? original.currentStatus.status
                : null}
            </span>
          );
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        overrideWidth: 100,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IOrderDetail } };
        }) => {
          const hasUndefinedParts =
            original.parts.filter(
              part => part.partName === UNDEFINED_PART || !!part.undefinedPartDescriptionId
            ).length > 0;
          return (
            <div className={classes.actionButtons}>
              <IconButton
                className={classes.editButton}
                color="primary"
                onClick={() => {
                  history.push(`/production/orders/order-management/${original.fieldOrderId}`);
                }}
                size="large"
                title="Edit"
              >
                <Edit />
              </IconButton>
              {!hasUndefinedParts && (
                <IconButton
                  title="Favorite"
                  className={classes.favoriteButton}
                  onClick={() => handleFavoriteClick(original)}
                  size="large"
                >
                  {original.isFavorite ? <Favorite /> : <FavoriteBorder />}
                </IconButton>
              )}
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, selectedOrders]);

  return (
    <OrderManagementPage title="Order Management">
      <div style={{ position: 'relative' }}>
        {(isBulkUpdating ||
          isSharing ||
          isLoadingAllowedUsers ||
          isRemovingFavorite ||
          isLoadingCSVData) && (
          <Loader type="overlay" position="centered">
            Loading...
          </Loader>
        )}
        <Grid container className={classes.titleWrapper}>
          <Grid item xs={12} className={classes.pageTitleWrapper}>
            <PageTitle marginBottom={false} showDivider={false} title="Order Management" />
          </Grid>
          <Grid item className={classes.infoWrapper}>
            <Box className={classes.subSelectors} display="flex">
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={isActive}
                    id="active-inactive"
                    disabled={isLoadingOrders}
                    onChange={handleSwitchChange}
                  />
                }
                label={isActive ? 'Pending' : 'Completed'}
              />
              {shouldShowSubSelectorDivider && allowedUsers?.length > 0 && (
                <Divider orientation="vertical" flexItem />
              )}
              <div className={classes.viewOrdersByContainer}>
                {allowedUsers?.length > 0 && (
                  <>
                    <span className={classes.viewOrdersBy}>View Orders By:</span>
                    <TextField
                      disabled={isLoadingAllowedUsers}
                      label={'Users'}
                      name={`Users`}
                      id={`sharedOrders`}
                      size="small"
                      className={classes.viewOrdersByDropdown}
                      value={selectedAllowedUsers}
                      select
                      SelectProps={{
                        multiple: true,
                        onClose: () => handleShare()
                      }}
                      onChange={event => {
                        setSelectedAllowedUsers(
                          typeof event.target.value === 'string'
                            ? event.target.value.split(',')
                            : event.target.value
                        );
                      }}
                    >
                      {allowedUsers &&
                        allowedUsers.map((whoCanView, index) => {
                          return (
                            <MenuItem key={`${index}`} value={whoCanView.accountName}>
                              {whoCanView.accountName}
                            </MenuItem>
                          );
                        })}
                    </TextField>
                  </>
                )}
              </div>
            </Box>
          </Grid>
        </Grid>
        <Divider />
        <OrderManagementFilters
          isLoading={isLoadingOrders}
          searchValue={searchValue}
          setSearchValue={setSearch}
          selectedVendorId={selectedVendorId}
          setSelectedVendorId={setSelectedVendorId}
          selectedOwner={selectedOwner}
          setSelectedOwner={setSelectedOwner}
          selectedOrderType={selectedOrderType}
          setSelectedOrderType={setSelectedOrderType}
          selectedDescription={selectedDescription}
          setSelectedDescription={setSelectedDescription}
          selectedLotId={selectedLotId}
          setSelectedLotId={setSelectedLotId}
          selectedNeighborhoodId={selectedNeighborhoodId}
          setSelectedNeighborhoodId={setSelectedNeighborhoodId}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          setParentOrderTypes={setParentOrderTypes}
          selectedDateRange={selectedDateRange}
          setSelectedDateRange={setSelectedDateRange}
          handleSearch={(
            data: {
              selectedOrderType: string;
              selectedNeighborhoodId: string;
              selectedStatus: string;
              selectedDescription: string;
              selectedOwner: string;
              selectedLotId: string;
              selectedVendorId: string;
            },
            clearSearch?: boolean
          ) => {
            setPage(0);
            if (clearSearch) {
              setSearchedValue('');
              setSearch('');
              handleUpdateFilters({
                ...persistentFilters,
                filters: {
                  ...persistentFilters.filters,
                  orderSearch: undefined
                },
                pageName: PAGE_NAME,
                environment: getEnvironment()
              });
            } else {
              const payload = {
                ...persistentFilters,
                filters: {
                  orderType: data?.selectedOrderType || undefined,
                  neighborhoodId: data?.selectedNeighborhoodId || undefined,
                  lotNumber: data?.selectedLotId || undefined,
                  status: data?.selectedStatus || undefined,
                  orderSearch: clearSearch ? undefined : searchValue || undefined,
                  orderPlacer: data?.selectedOwner || undefined,
                  descriptionId: data?.selectedDescription || undefined,
                  vendorId: data?.selectedVendorId || undefined,
                  startDate: selectedDateRange?.startDate?.toISOString() || undefined,
                  endDate: selectedDateRange?.endDate?.toISOString() || undefined,
                  active: isActive
                },
                pageName: PAGE_NAME,
                environment: getEnvironment()
              };
              handleUpdateFilters(payload);
            }
          }}
          applyFilters={(
            data: {
              selectedOrderType: string;
              selectedNeighborhoodId: string;
              selectedStatus: string;
              selectedDescription: string;
              selectedOwner: string;
              selectedLotId: string;
              selectedVendorId: string;
              isActive: boolean;
            },
            clearFilters?: boolean,
            clearSearch?: boolean
          ) => {
            if (clearFilters) {
              const payload = {
                undefined,
                pageName: PAGE_NAME,
                environment: getEnvironment()
              };
              handleUpdateFilters(payload);
            } else {
              setPage(0);
              const payload = {
                ...persistentFilters,
                filters: {
                  orderType: data?.selectedOrderType || undefined,
                  neighborhoodId: data?.selectedNeighborhoodId || undefined,
                  lotNumber: data?.selectedLotId || undefined,
                  status: data?.selectedStatus || undefined,
                  orderSearch: clearSearch ? undefined : searchValue || undefined,
                  orderPlacer: data?.selectedOwner || undefined,
                  descriptionId: data?.selectedDescription || undefined,
                  vendorId: data?.selectedVendorId || undefined,
                  startDate: selectedDateRange?.startDate?.toISOString() || undefined,
                  endDate: selectedDateRange?.endDate?.toISOString() || undefined,
                  active: isActive
                },
                pageName: PAGE_NAME,
                environment: getEnvironment()
              };
              handleUpdateFilters(payload);
            }
          }}
          hasAppliedFiltersGlobal={Object.entries(persistentFilters?.filters || {}).some(
            ([key, value]) => key !== 'active' && value !== undefined
          )}
        />
        {searchedValue && (
          <SearchSection
            searchedValue={searchedValue}
            handleClearSearch={() => {
              setSearch('');
              setSearchedValue('');
              setPage(0);
              const payload = {
                ...persistentFilters,
                filters: {
                  ...persistentFilters.filters,
                  orderSearch: undefined
                },
                pageName: PAGE_NAME,
                environment: getEnvironment()
              };
              handleUpdateFilters(payload);
            }}
          />
        )}
        <Divider />
        <div className={classes.actionsWrapper}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllSelected}
                id="orders-checkbox"
                onChange={() => {
                  if (isAllSelected) {
                    setSelectedOrders([]);
                  } else {
                    // only grab non placed orders
                    setSelectedOrders(
                      orders
                        .filter(order => order.currentStatus.status !== 'Placed')
                        .filter(order => order.canApproveOrder)
                        .filter(order => order.parts.length > 0)
                    );
                  }
                  setSelectedAll(!isAllSelected);
                }}
              />
            }
            label=""
          />
          <div style={{ flex: 1 }}>
            <TextField
              id="orders-actions"
              name="orders-actions"
              inputProps={{ maxLength: 255 }}
              select
              label="Bulk Statuses"
              className={classes.actionsDropdownFixed}
              disabled={selectedOrders.length === 0 || isLoadingOrders}
              size="small"
              value=""
              onChange={e => {
                const result = window.confirm(
                  `Are you sure you want to change status on ${
                    selectedOrders.length === 0 ? 'this order' : 'these orders'
                  }?`
                );
                if (result) {
                  handleBulkStatusChange(e.target.value);
                }
              }}
            >
              {(isBillingAdmin || isFinancialAdmin ? ['Approved', 'Placed'] : ['Approved']).map(
                (action: string, index: number) => {
                  return (
                    <MenuItem className={classes.actionMenuItem} key={`${index}`} value={action}>
                      {action}
                    </MenuItem>
                  );
                }
              )}
            </TextField>
          </div>

          <Button
            id={'csv_download'}
            color="primary"
            variant="contained"
            disabled={!isActive ? isLoadingOrders : isLoadingCSVData}
            onClick={async () => {
              try {
                const allOrders = await fetchAllOrders();
                const BOM = String.fromCharCode(0xfeff);
                // Initialize CSV with headers
                let csv =
                  'Date/Time,Order No.,Blinq No.,Order Type,Order Desc.,Job,Owner,Parts,Total,Status\n';

                // Add order values to the csv
                // If the rows are active, remove Order No. from CSV
                csv = formatCSVColumns(
                  isActive ? allOrders : orders,
                  !isActive ? csv : csv.replace('Order No.,', ''),
                  orderTypes,
                  isActive
                );

                // Create an file the user can download
                const el = document.createElement('a');
                el.href = `data:text/csv;charset=UTF-8,${encodeURIComponent(`${BOM}${csv}`)}`;
                el.target = '_blank';
                el.download = `myOrders_${format(
                  parseISO(new Date().toISOString()),
                  'yyyy_LL_dd_hh_mm_ss'
                )}.csv`;
                // Download the file
                el.click();
              } catch (error) {
                console.log(error);
              }
            }}
            startIcon={<CloudDownload />}
          >
            CSV
          </Button>
        </div>
        <Divider />
        <Table
          columns={columns.filter(column =>
            isActive ? column.accessor !== 'sageOrderNumbers' : true
          )}
          data={orders}
          isLoading={isLoadingOrders}
          hidePagination
          ResponsiveComponent={isMobile ? MobileOrderManagement : undefined}
          mobileBreakPoint={1400}
          mobileProps={{
            handleEdit: (original: IOrderDetail) => {
              history.push(`/production/orders/order-management/${original.fieldOrderId}`);
            },
            handleFavorite: (original: IOrderDetail) => handleFavoriteClick(original),
            handleBulkStatusChange: (val: string) => handleBulkStatusChange(val),
            selectedOrders,
            setSelectedOrders: val => setSelectedOrders(val),
            isActive: isActive
          }}
        />
        {!isLoadingOrders && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
        <FavoriteModal
          open={!!currentFavoriteOrder}
          handleClose={(shouldRefresh?: boolean) => {
            setCurrentFavoriteOrder(null);
            if (shouldRefresh) {
              fetchOrders();
            }
          }}
          currentOrder={currentFavoriteOrder}
        />
      </div>
    </OrderManagementPage>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionButtons: {
    display: 'flex'
  },
  csvButton: {
    padding: 3
  },
  editButton: {
    padding: 3
  },
  favoriteButton: {
    padding: 3,
    color: theme.palette.grey[400]
  },
  approveButton: {
    padding: 3
  },
  clearSearchButton: {
    '@media (min-width: 408px)': {
      marginTop: 3,
      marginLeft: theme.spacing(1)
    }
  },
  searchWrapper: {
    margin: theme.spacing(1, 0)
  },
  statusRed: {
    color: theme.palette.error.main
  },
  pageTitleWrapper: {
    '@media (min-width: 768px)': {
      flex: 1
    }
  },
  titleWrapper: {
    marginBottom: theme.spacing(0.5),
    alignItems: 'center'
  },
  infoWrapper: {
    '@media (min-width: 768px)': {
      textAlign: 'left',
      alignItems: 'center'
    },
    flexWrap: 'wrap'
  },

  csvWrapper: {
    textAlign: 'right',
    '@media (max-width: 768px)': {
      marginBottom: theme.spacing(1)
    }
  },
  actionsDropdownFixed: {
    marginRight: 8,
    width: '7rem',
    '@media (min-width: 768px)': {
      marginRight: theme.spacing(2),
      width: '10rem'
    }
  },
  actionsDropdownAuto: {
    marginRight: theme.spacing(2),
    width: 'auto'
  },
  actionsWrapper: {
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    alignItems: 'center'
  },
  allCheckboxWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    flex: 1
  },
  buttonGroupWrapper: {
    marginRight: theme.spacing(2),

    '&& label': {
      margin: 0
    },
    '&& button': {
      background: theme.palette.common.white,
      padding: 0
    },
    '&& .MuiButtonBase-root': {
      borderRadius: 0
    }
  },
  allCheckboxPopper: {
    zIndex: 10
  },
  actionMenuItem: {
    whiteSpace: 'pre-wrap'
  },
  tableCheckbox: {
    marginRight: 0
  },
  undefinedPart: {
    color: lighten(theme.palette.error.main, 0.5)
  },
  backcharge: {
    color: orange[700]
  },
  viewOrdersByContainer: {
    display: 'flex',
    alignItems: 'center',
    margin: '1rem',
    '@media (max-width: 768px)': {
      justifyContent: 'center',
      flexWrap: 'wrap'
    }
  },
  viewOrdersBy: {
    marginRight: theme.spacing(1)
  },
  subSelectors: {
    display: 'flex',
    '@media (max-width: 500px)': {
      flexWrap: 'wrap',
      justifyContent: 'center'
    }
  },
  viewOrdersByDropdown: {
    minWidth: '152px'
  }
}));
